/**
 * DrapeQualityPanel - Display draping quality metrics
 * 
 * Shows quality metrics from the draping operation with color-coded indicators.
 */

import React from 'react'
import type { DrapeQualityMetrics } from '../../../services/types'
import './DrapeQualityPanel.css'

interface DrapeQualityPanelProps {
  metrics: DrapeQualityMetrics
}

/**
 * Get quality level based on hit rate
 */
function getQualityLevel(hitRate: number): 'good' | 'warning' | 'poor' {
  if (hitRate > 0.95) return 'good'
  if (hitRate > 0.8) return 'warning'
  return 'poor'
}

/**
 * Get quality badge color class
 */
function getQualityClass(level: 'good' | 'warning' | 'poor'): string {
  switch (level) {
    case 'good': return 'quality-good'
    case 'warning': return 'quality-warning'
    case 'poor': return 'quality-poor'
  }
}

/**
 * Format number to fixed decimals
 */
function formatNumber(value: number, decimals: number = 2): string {
  return value.toFixed(decimals)
}

/**
 * Format percentage
 */
function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

/**
 * DrapeQualityPanel component
 */
export const DrapeQualityPanel: React.FC<DrapeQualityPanelProps> = ({ metrics }) => {
  const qualityLevel = getQualityLevel(metrics.hit_rate)
  const qualityClass = getQualityClass(qualityLevel)

  return (
    <div className="drape-quality-panel">
      <h4>Draping Quality</h4>
      
      <div className="quality-metrics">
        <div className={`metric-row ${qualityClass}`}>
          <span className="metric-label">Hit Rate:</span>
          <span className="metric-value">{formatPercentage(metrics.hit_rate)}</span>
        </div>

        <div className="metric-row">
          <span className="metric-label">Max Deviation:</span>
          <span className="metric-value">{formatNumber(metrics.max_deviation_mm)} mm</span>
        </div>

        <div className="metric-row">
          <span className="metric-label">Mean Deviation:</span>
          <span className="metric-value">{formatNumber(metrics.mean_deviation_mm)} mm</span>
        </div>

        <div className="metric-row">
          <span className="metric-label">Smoothness:</span>
          <span className="metric-value">{formatPercentage(metrics.boundary_smoothness)}</span>
        </div>
      </div>

      <div className={`quality-badge ${qualityClass}`}>
        {qualityLevel === 'good' && '✓ Excellent Quality'}
        {qualityLevel === 'warning' && '⚠ Acceptable Quality'}
        {qualityLevel === 'poor' && '✗ Poor Quality - Review Required'}
      </div>
    </div>
  )
}
